import { useState } from 'react';
import hash from 'object-hash';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import style from '@core/Page/LeftMenu/LeftMenu.module.scss';
import itemStyle from '@core/Page/LeftMenu/NavigationItem/NavigationItem.module.scss';

import { ClickOutsideAlerter } from '@components/ClickOutsideAlerter';
import { FixedIcon, Icon, IconType } from '@components/Icon';

import { NavigationItem } from '@core/Page/LeftMenu/NavigationItem';
import { UserProfile } from '@core/Page/LeftMenu/UserProfile';
import { ChangeLanguageButton } from '@components/ActionButtons/ChangeLanguageButton';
import { translate } from '@chaika/i18n/helper';
import { AppPortal } from '@components/AppPortal';
import { SupportWidget } from '@components/SupportWidget';
import { useSupportInbox } from '@components/SupportWidget/hooks/useSupportInbox';
import { LeftMenuAttributes } from 'chaika-test-attributes';

import { NavigationGroup } from './components/NavigationGroup';
import { useLeftMenu } from './hooks/useLeftMenu';
import { useScrollShadows } from './hooks/useScrollShadows';

export interface LeftMenuParams {
    additionalContent?: React.ComponentType;
}

export const LeftMenu = observer(({ additionalContent: AdditionalContent }: LeftMenuParams) => {
    const {
        navigationItems,
        isOpen,
        isTable,
        isOpenAndTable,
        withPhoneWidget,
        withSupportWidget,
        isPhoneWidgetOpen,
        isMobile,
        disableOpen,
        toggleIsLeftMenuOpen,
        closeMenu,
        onLinkClick,
        onSubItemTitleClick,
        onLogoutClick,
        onTogglePhoneWidgetToggle,
    } = useLeftMenu();
    const { isScrolledFromTop, hasContentBelow, topSentinelRef, bottomSentinelRef } = useScrollShadows();

    return (
        <ClickOutsideAlerter onClickOutside={closeMenu}>
            <div className={classNames([style.root, isOpen && style.open])}>
                <div className={style.leftMenu}>
                    {isOpenAndTable && (
                        <div
                            className={style.closeIcon}
                            onClick={toggleIsLeftMenuOpen}
                        >
                            <Icon type={IconType.CROSS} />
                        </div>
                    )}
                    <div className={classNames([style.header, isScrolledFromTop && style.header_withShadow])}>
                        <div className={style.logo}>
                            {isOpen ? <Icon type={IconType.LOGO} /> : FixedIcon.getLogoShort()}
                        </div>
                        <div className={style.changeLanguageButton}>
                            <ChangeLanguageButton />
                        </div>
                    </div>
                    {isTable && AdditionalContent && (
                        <div
                            className={style.additionalContentWrapper}
                            onClick={onLinkClick}
                        >
                            <AdditionalContent />
                        </div>
                    )}
                    <div className={style.navigationMenuWrapper}>
                        <div ref={topSentinelRef} />
                        <div className={style.navigationMenu}>
                            {navigationItems.map(item =>
                                item.subItems ? (
                                    <NavigationGroup
                                        key={hash(item)}
                                        item={item}
                                        subItems={item.subItems}
                                        isMenuOpen={isOpen}
                                        onLinkClick={onLinkClick}
                                        onSubItemTitleClick={onSubItemTitleClick}
                                    />
                                ) : (
                                    <NavigationItem
                                        key={hash(item)}
                                        title={translate(item.title)}
                                        dataTest={item.dataTest}
                                        link={item.link}
                                        blank={item.blank}
                                        iconType={item.iconType}
                                        onLinkClick={onLinkClick}
                                    />
                                ),
                            )}
                            {withPhoneWidget && (
                                <div
                                    className={classNames([
                                        itemStyle.root,
                                        style.widgetToggle,
                                        isPhoneWidgetOpen && itemStyle.active,
                                    ])}
                                    onClick={onTogglePhoneWidgetToggle}
                                >
                                    <div className={classNames([itemStyle.icon])}>
                                        <Icon type={IconType.PHONE} />
                                    </div>
                                    <div className={itemStyle.title}>{translate('phone')}</div>
                                </div>
                            )}
                            {withSupportWidget && (
                                <SupportMenuItem
                                    isMobile={isMobile}
                                    isLeftMenuOpen={isOpen}
                                />
                            )}
                        </div>
                        <div ref={bottomSentinelRef} />
                    </div>
                    <div className={classNames([style.footer, hasContentBelow && style.footer_withShadow])}>
                        <UserProfile isOpen={isOpen} />
                        {isOpen && (
                            <div
                                className={style.logoutButton}
                                onClick={onLogoutClick}
                            >
                                {translate('exit')}
                            </div>
                        )}
                        {!disableOpen && (
                            <div
                                className={style.openMenuButton}
                                onClick={toggleIsLeftMenuOpen}
                            >
                                <div className={style.openMenuIcon}>
                                    <Icon type={IconType.ARROW_DOWN} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ClickOutsideAlerter>
    );
});

interface SupportMenuItemProps {
    isMobile: boolean;
    isLeftMenuOpen: boolean;
}

function SupportMenuItem({ isMobile, isLeftMenuOpen }: SupportMenuItemProps) {
    const [isWidgetOpen, setIsWidgetOpen] = useState(false);
    const inbox = useSupportInbox();

    const handleToggle = (): void => {
        if (isWidgetOpen) {
            inbox.closeTicket();
        } else {
            inbox.resetFilter();
        }
        setIsWidgetOpen(isOpen => !isOpen);
    };

    const handleClose = (): void => {
        inbox.closeTicket();
        setIsWidgetOpen(false);
    };

    return (
        <>
            <div
                className={classNames([itemStyle.root, style.widgetToggle, isWidgetOpen && itemStyle.active])}
                data-test={LeftMenuAttributes.Support}
                data-testid={LeftMenuAttributes.Support}
                onClick={handleToggle}
            >
                <div className={classNames(itemStyle.icon, style.supportIcon)}>
                    <Icon type={IconType.SUPPORT} />
                    {inbox.totalUnread > 0 && (
                        <span
                            className={style.supportUnreadBadge}
                            data-test={'leftMenu_supportUnreadBadge'}
                            data-testid={'leftMenu_supportUnreadBadge'}
                            aria-label={translate('supportUnreadCount', { $count: String(inbox.totalUnread) })}
                        >
                            {inbox.totalUnread > 99 ? '99+' : inbox.totalUnread}
                        </span>
                    )}
                </div>
                <div className={itemStyle.title}>{translate('supportTitle')}</div>
            </div>
            {isWidgetOpen && (
                <AppPortal>
                    <SupportWidget
                        inbox={inbox}
                        isMobile={isMobile}
                        isLeftMenuOpen={isLeftMenuOpen}
                        onClose={handleClose}
                    />
                </AppPortal>
            )}
        </>
    );
}
